import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ChatService, ConversationMessageDto } from 'src/app/core/services/chat.service';
import { LoginService } from 'src/app/auth/login.service';
import { environment } from 'src/environments/environment';
import { SseService } from 'src/app/core/services/sse.service';
import { Subscription } from 'rxjs';

interface ChatBubbleMessage {
  id: string;
  from: 'me' | 'support' | 'system';
  text: string;
  time?: Date;
  avatarUrl?: string | null;
  imageUrl?: string | null;
}

@Component({
  selector: 'app-chat-bubble',
  templateUrl: './chat-bubble.component.html',
  styleUrls: ['./chat-bubble.component.css'],
})
export class ChatBubbleComponent implements OnInit {
  /** show/hide bubble */
  @Input() visible = true;

  /** optional unread badge */
  @Input() unread = 0;

  /** emit when user sends */
  @Output() sendMessage = new EventEmitter<string>();

  isOpen = false;
  draft = '';
  messages: ChatBubbleMessage[] = [];

  /** Current conversation id (set from login user detail) */
  conversationId = 0;

  loading = false;

  @ViewChild('list') list?: ElementRef<HTMLDivElement>;
  @ViewChild('input') input?: ElementRef<HTMLInputElement>;

  private myUserCode = '';
  private sseSub?: Subscription;

  // senderAvatar hiện trả về dạng path (uploads/...) và được serve dưới /api
  private readonly assetBaseUrl = environment.apiUrl.replace(/\/+$/, '');

  constructor(
    private chatService: ChatService,
    private loginService: LoginService,
    private sseService: SseService,
  ) {}

  ngOnInit(): void {
    const userDetail = this.loginService.getUserDetail();
    this.myUserCode = String(userDetail?.userCode || userDetail?.code || '').toLowerCase();

    const convId = userDetail?.conversationId;
    const convIdNumber = typeof convId === 'number' ? convId : Number(convId || 0);

    this.conversationId = convIdNumber;

    // Optionally preload minimal message; the real history loads when opening
    this.messages = [
      { id: 'sys-1', from: 'system', text: 'Xin chào! Bạn cần hỗ trợ gì?' },
    ];

    // Listen for global SSE events (named event: new_message)
    this.sseSub = this.sseService.onEvent$().subscribe((ev) => {
      const t = String(ev?.json?.type || '').toLowerCase();
      const dto = (ev?.json?.data ?? ev?.json) as any;

      if (t === 'new_message' && dto) {
        this.applyIncomingMessage(dto as ConversationMessageDto);
        return;
      }

      if (t === 'delete_chat') {
        this.messages = [
          { id: 'sys-1', from: 'system', text: 'Nội dung chat đã được admin xoá.' },
        ];
        this.unread = 0;

        if (this.isOpen) {
          setTimeout(() => this.scrollBottom(), 0);
        }
        return;
      }
    });
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      // reset unread when opening
      this.unread = 0;

      this.loadHistory();
      setTimeout(() => {
        this.scrollBottom();
        this.input?.nativeElement?.focus();
      }, 0);
    }
  }

  private applyIncomingMessage(dto: ConversationMessageDto): void {
    const convId = Number((dto as any)?.conversationId || 0);
    if (!this.conversationId || convId !== this.conversationId) return;

    const senderCode = String((dto as any)?.senderCode || '').toLowerCase();
    const from: ChatBubbleMessage['from'] = senderCode && this.myUserCode && senderCode === this.myUserCode
      ? 'me'
      : 'support';

    const rawTime = (dto as any)?.createDate || (dto as any)?.createdAt;
    const parsed = rawTime ? new Date(rawTime) : undefined;
    const time = parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date();

    const rawAvatar = (dto as any)?.senderAvatar ? String((dto as any)?.senderAvatar) : '';
    const avatarUrl = rawAvatar
      ? (rawAvatar.startsWith('http') ? rawAvatar : `${this.assetBaseUrl}/${rawAvatar.replace(/^\/+/, '')}`)
      : null;

    const isFile = String((dto as any)?.type || '').toUpperCase() === 'FILE';
    const rawFileUrl = (dto as any)?.fileUrl ? String((dto as any)?.fileUrl) : '';
    const imageUrl = isFile && rawFileUrl
      ? (rawFileUrl.startsWith('http') ? rawFileUrl : `${this.assetBaseUrl}/${rawFileUrl.replace(/^\/+/, '')}`)
      : null;

    const msg: ChatBubbleMessage = {
      id: String((dto as any)?.id ?? `sse-${Date.now()}`),
      from,
      text: isFile ? '' : String((dto as any)?.message || ''),
      time,
      avatarUrl,
      imageUrl,
    };

    if (this.messages.some(m => m.id === msg.id)) return;

    this.messages = [...this.messages, msg];

    // increase unread if bubble is closed
    if (!this.isOpen) {
      this.unread = (Number(this.unread || 0) + 1);
      return;
    }

    // Only auto-scroll when bubble is open
    setTimeout(() => this.scrollBottom(), 0);
  }

  private loadHistory(): void {
    this.loading = true;
    this.chatService.getConversation(this.conversationId).subscribe({
      next: (res: any) => {
        const rows: ConversationMessageDto[] = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : Array.isArray(res?.data?.content)
              ? res.data.content
              : [];

        const mapped: ChatBubbleMessage[] = rows
          .filter(r => (String(r?.message ?? '').trim().length > 0) || String(r?.type || '').toUpperCase() === 'FILE')
          .map((r, idx) => {
            const msgText = String(r.message || '');
            const senderCode = String(r.senderCode || '').toLowerCase();

            const from: ChatBubbleMessage['from'] = senderCode && this.myUserCode && senderCode === this.myUserCode
              ? 'me'
              : 'support';

            const rawTime = r.createDate || r.createdAt; // server time
            const parsed = rawTime ? new Date(rawTime) : undefined;
            const time = parsed && !Number.isNaN(parsed.getTime()) ? parsed : undefined;

            const rawAvatar = r.senderAvatar ? String(r.senderAvatar) : '';
            const avatarUrl = rawAvatar
              ? (rawAvatar.startsWith('http') ? rawAvatar : `${this.assetBaseUrl}/${rawAvatar.replace(/^\/+/, '')}`)
              : null;

            const isFile = String(r.type || '').toUpperCase() === 'FILE';
            const rawFileUrl = r.fileUrl ? String(r.fileUrl) : '';
            const imageUrl = isFile && rawFileUrl
              ? (rawFileUrl.startsWith('http') ? rawFileUrl : `${this.assetBaseUrl}/${rawFileUrl.replace(/^\/+/, '')}`)
              : null;

            return {
              id: String(r.id ?? `api-${idx}`),
              from,
              text: isFile ? '' : msgText,
              time,
              avatarUrl,
              imageUrl,
            };
          })
          .sort((a, b) => (a.time?.getTime() ?? 0) - (b.time?.getTime() ?? 0));

        // insert day dividers (system messages) like FB
        const withDividers: ChatBubbleMessage[] = [];
        let lastDayKey = '';
        for (const m of mapped) {
          const dayKey = m.time ? this.toDayKey(m.time) : '';
          if (dayKey && dayKey !== lastDayKey) {
            withDividers.push({
              id: `day-${dayKey}`,
              from: 'system',
              text: this.formatDayLabel(m.time!),
              time: m.time,
            });
            lastDayKey = dayKey;
          }
          withDividers.push(m);
        }

        this.messages = [
          { id: 'sys-1', from: 'system', text: 'Xin chào! Bạn cần hỗ trợ gì?' },
          ...withDividers,
        ];
        this.loading = false;
        setTimeout(() => this.scrollBottom(), 0);
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  private toDayKey(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private formatDayLabel(d: Date): string {
    // dd/MM/yyyy
    const day = String(d.getDate()).padStart(2, '0');
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const y = d.getFullYear();
    return `${day}/${m}/${y}`;
  }

  submit(): void {
    const text = String(this.draft || '').trim();
    if (!text) return;

    const clientTempId = `web-${Date.now()}`;

    // optimistic UI: use client time until server returns createDate
    const msg: ChatBubbleMessage = { id: clientTempId, from: 'me', text, time: new Date() };
    this.messages = [...this.messages, msg];
    this.draft = '';
    this.sendMessage.emit(text);

    // ensure scroll after DOM updates
    setTimeout(() => this.scrollBottom(), 0);

    this.chatService.addMessage({
      conversationId: this.conversationId,
      groupId: '',
      message: text,
      clientTempId,
    }).subscribe({
      next: (res: any) => {
        // If backend returns created message with createDate, update optimistic item
        const created = res?.data ?? res;
        const serverTimeRaw = created?.createDate || created?.createdAt;
        const serverTime = serverTimeRaw ? new Date(serverTimeRaw) : undefined;
        const okTime = serverTime && !Number.isNaN(serverTime.getTime()) ? serverTime : undefined;

        if (okTime) {
          this.messages = this.messages.map(m => m.id === clientTempId ? ({ ...m, time: okTime } as any) : m);
        }

        // keep pinned to bottom
        setTimeout(() => this.scrollBottom(), 0);
      },
      error: () => {
        setTimeout(() => this.scrollBottom(), 0);
      }
    });
  }

  async onPickFile(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) return;

    // reset input so picking same file again still triggers change
    if (input) input.value = '';

    // optimistic preview
    const dataUrl = await this.readAsDataUrl(file);
    const clientTempId = `img-${Date.now()}`;

    this.messages = [...this.messages, {
      id: clientTempId,
      from: 'me',
      text: '',
      time: new Date(),
      imageUrl: dataUrl,
    }];

    setTimeout(() => this.scrollBottom(), 0);

    this.chatService.sendFile(this.conversationId, file).subscribe({
      next: () => {
        // reload to get server fileUrl + createDate
        this.loadHistory();
      },
      error: () => setTimeout(() => this.scrollBottom(), 0),
    });
  }

  private readAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('read file failed'));
      reader.onload = () => resolve(String(reader.result || ''));
      reader.readAsDataURL(file);
    });
  }

  private scrollBottom(): void {
    const el = this.list?.nativeElement;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (this.isOpen) this.close();
  }

  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    this.sseSub?.unsubscribe();
  }

  close(): void {
    this.isOpen = false;
  }
}
